import type { ReactNode } from 'react'
import { MdCheck, MdLoop, MdOutlineFlag, MdOutlineInventory2, MdOutlineUnarchive, MdPlayArrow } from 'react-icons/md'

import { cx } from '@/Ship/Helpers/cssClass'

import type { TaskAction, TaskActionIcon } from './taskStatus'

import styles from './TaskActionButtons.module.css'

/** Піктограми дій водія — і на кнопках, і у вікні підтвердження */
export const ACTION_ICONS: Record<TaskActionIcon, ReactNode> = {
    play: <MdPlayArrow />,
    load: <MdOutlineInventory2 />,
    arrive: <MdOutlineFlag />,
    unload: <MdOutlineUnarchive />,
    loop: <MdLoop />,
    check: <MdCheck />,
}

interface TaskActionButtonsProps {
    actions: TaskAction[]
    loading: boolean
    onRun: (action: TaskAction) => void
    /** flush — смужка впритул до країв картки (одна дія); padded — з відступами й радіусами */
    layout?: 'flush' | 'padded'
}

/**
 * Кнопки дій водія за статусом. Одна дія — суцільна помаранчева на всю ширину.
 * Кілька — з ієрархією: завершальна (success) заливкою, проміжна («Нова ходка») контуром,
 * щоб два яскравих блоки не сперечалися між собою; у кожної — піктограма.
 */
export const TaskActionButtons = ({ actions, loading, onRun, layout = 'padded' }: TaskActionButtonsProps) => {
    if (actions.length === 0) return null

    const multi = actions.length > 1
    const variant = (action: TaskAction) => {
        if (action.tone === 'success') return 'pwa-btn--success'
        return multi ? 'pwa-btn--outline' : 'pwa-btn--primary'
    }

    return (
        <div
            className={[
                cx(styles, 'wrap'),
                cx(styles, multi ? 'wrap--multi' : 'wrap--single'),
                cx(styles, `wrap--${layout}`),
            ].join(' ')}
        >
            {actions.map((action) => (
                <button
                    key={action.next}
                    type="button"
                    className={`pwa-btn ${variant(action)} ${cx(styles, 'btn')}`}
                    disabled={loading}
                    onClick={() => onRun(action)}
                >
                    <span className={cx(styles, 'icon')} aria-hidden>
                        {ACTION_ICONS[action.icon]}
                    </span>
                    <span className={cx(styles, 'label')}>{loading ? 'Зачекайте…' : action.label}</span>
                </button>
            ))}
        </div>
    )
}
